import { Fragment, useEffect, useRef, useState } from "react";
import { api, ApiError, attachmentToFile } from "../lib/api";
import { useStore } from "../lib/store";
import type { Conversation, Message } from "@swift/shared";
import { renderMarkdown } from "../lib/markdown";
import { loadSettings } from "../lib/settings";
import { getSocket } from "../lib/socket";
import { isTauri, saveAndOpenBlob } from "../lib/tauri";
import ReactionRow from "./ReactionRow";
import PollCard from "./PollCard";
import LocationCard from "./LocationCard";
import { scrollToMessage } from "../lib/scrollToMessage";
import {
  IconPin,
  IconReply,
  IconForward,
  IconPencil,
  IconTrash,
} from "./icons";

type Props = { conversation: Conversation };

const PAGE_SIZE = 50;
// Vaste set quick-reacties in de hover-toolbar van elk bericht. Bewust
// hetzelfde rijtje als de ReactionRow-popover (consistent gevoel), maar
// dan eenmalig zichtbaar zonder eerst een "+" te hoeven aanklikken.
const QUICK_REACT_EMOJI = ["👍", "❤️", "😂", "🎉", "👀", "🙏"];

function sameDay(a: string, b: string): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

/** "Vandaag" / "Gisteren" / "12 mei 2026" */
function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(iso, today.toISOString())) return "Vandaag";
  if (sameDay(iso, yesterday.toISOString())) return "Gisteren";
  return d.toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// Stabiele referenties voor 'leeg' — zo geeft de zustand-selector niet bij
// elke render een nieuw [] terug. Zonder dit zou useSyncExternalStore een
// rerender-loop triggeren ("Maximum update depth exceeded").
const EMPTY_MESSAGES: Message[] = [];
const EMPTY_TYPING: number[] = [];

export default function MessageStream({ conversation }: Props) {
  const messages = useStore((s) => s.messagesByConv[conversation.id] ?? EMPTY_MESSAGES);
  const hasMore = useStore((s) => s.hasMoreByConv[conversation.id] ?? false);
  const users = useStore((s) => s.users);
  const me = useStore((s) => s.me);
  const myUsername = me?.username;
  const setMessages = useStore((s) => s.setMessages);
  const prepend = useStore((s) => s.prependMessages);
  const setTyping = useStore((s) => s.typingByConv[conversation.id] ?? EMPTY_TYPING);
  const containerRef = useRef<HTMLDivElement>(null);
  const inhoudRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);

  /*
   * Staan we aan de onderkant vastgeklikt?
   *
   * Nodig omdat de hoogte van een gesprek nog verandert nadat het er staat:
   * afbeeldingen komen later binnen, en een audio-speler krijgt pas hoogte als
   * de metadata er is. Het gevolg was dat je een gesprek opende, netjes
   * onderaan begon, en er daarna vanzelf boven belandde omdat alles onder je
   * groeide.
   *
   * Zolang dit true is volgen we die groei. Zodra je zelf omhoog scrolt niet
   * meer - dan ben je iets aan het teruglezen, en meeschuiven is dan het
   * laatste wat je wilt.
   */
  const onderaan = useRef(true);

  // Initial load
  useEffect(() => {
    void (async () => {
      if ((useStore.getState().messagesByConv[conversation.id] ?? []).length > 0) return;
      const { messages: msgs } = await api.listMessages(conversation.id, undefined, PAGE_SIZE);
      setMessages(conversation.id, msgs, msgs.length === PAGE_SIZE);
      requestAnimationFrame(() => scrollToBottom());
    })();
  }, [conversation.id, setMessages]);

  /* Een ander gesprek begint onderaan - ook als de berichten al in het geheugen
     zaten, want dan slaat de lading hierboven over en scrolde er niets. */
  useEffect(() => {
    onderaan.current = true;
    requestAnimationFrame(() => scrollToBottom());
  }, [conversation.id]);

  /* Groeit de inhoud terwijl we onderaan staan, dan schuiven we mee. Een
     ResizeObserver op de inhoud vangt alles: een afbeelding die binnenkomt, een
     audio-speler die hoogte krijgt, een lettertype dat laadt. Beter dan
     luisteren naar het laden van afbeeldingen alleen, want dan mis je de rest. */
  useEffect(() => {
    const inhoud = inhoudRef.current;
    if (!inhoud || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (onderaan.current) scrollToBottom();
    });
    ro.observe(inhoud);
    return () => ro.disconnect();
  }, [conversation.id]);

  // Auto-scroll on new messages if near bottom; markeert dan meteen ook
  // als gelezen zodat het ongelezen-getalletje in de zijbalk niet
  // blijft hangen terwijl je naar het bericht staat te kijken.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || messages.length === 0) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance < 120) {
      scrollToBottom();
      const lastId = messages[messages.length - 1]!.id;
      const lastRead =
        useStore.getState().conversations[conversation.id]?.last_read_message_id ?? 0;
      if (lastId > lastRead) {
        useStore.getState().setLastRead(conversation.id, lastId);
        getSocket()?.emit("read:advance", {
          conversation_id: conversation.id,
          last_read_message_id: lastId,
        });
      }
    }
  }, [messages.length, conversation.id]);

  // Advance read marker when scrolled to bottom
  useEffect(() => {
    const el = containerRef.current;
    if (!el || messages.length === 0) return;
    const onScroll = () => {
      // Composer luistert hierop om z'n attachment-sheet te sluiten bij
      // scrollen — losgekoppeld van deze module zodat MessageStream niet
      // hoeft te weten wat er onder 'm hangt.
      window.dispatchEvent(new CustomEvent("swift:stream-scroll"));

      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
      onderaan.current = atBottom;
      if (atBottom) {
        const lastId = messages[messages.length - 1]!.id;
        const lastRead = useStore.getState().conversations[conversation.id]?.last_read_message_id ?? 0;
        if (lastId > lastRead) {
          // Optimistic: meteen lokaal markeren zodat de unread-badge
          // direct verdwijnt; de server bevestigt later via read:advanced.
          useStore.getState().setLastRead(conversation.id, lastId);
          getSocket()?.emit("read:advance", {
            conversation_id: conversation.id,
            last_read_message_id: lastId,
          });
        }
      }
    };
    el.addEventListener("scroll", onScroll);
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [conversation.id, messages]);

  async function loadOlder() {
    if (!hasMore || loading || messages.length === 0) return;
    setLoading(true);
    const el = containerRef.current;
    const prevHeight = el?.scrollHeight ?? 0;
    const { messages: older } = await api.listMessages(
      conversation.id,
      messages[0]!.id,
      PAGE_SIZE,
    );
    prepend(conversation.id, older, older.length === PAGE_SIZE);
    requestAnimationFrame(() => {
      if (el) el.scrollTop = el.scrollHeight - prevHeight;
    });
    setLoading(false);
  }

  function scrollToBottom() {
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }

  return (
    <div className="flex-1 overflow-y-auto bg-slate-900 px-4 py-3" ref={containerRef}>
      {/* Alles in een eigen laag: de ResizeObserver moet de hoogte van de
          INHOUD kunnen meten, en die van de scrollende doos zelf verandert
          nooit. */}
      <div ref={inhoudRef}>
      {hasMore && (
        <div className="mb-3 flex justify-center">
          <button
            onClick={loadOlder}
            className="rounded-md bg-slate-700 px-3 py-1 text-xs text-slate-200 hover:bg-slate-600 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Bezig…" : "Oudere berichten"}
          </button>
        </div>
      )}
      {messages.map((m, i) => {
        const prev = messages[i - 1];
        const newDay = !prev || !sameDay(prev.created_at, m.created_at);
        const sameSender =
          !newDay &&
          prev &&
          prev.sender_id === m.sender_id &&
          Date.parse(m.created_at) - Date.parse(prev.created_at) < 5 * 60 * 1000;
        const sender = users[m.sender_id];
        return (
          <Fragment key={m.id}>
            {newDay && (
              <div className="my-3 flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-700" />
                <span className="text-xs font-medium text-slate-500">
                  {dayLabel(m.created_at)}
                </span>
                <div className="h-px flex-1 bg-slate-700" />
              </div>
            )}
            <MessageItem
              message={m}
              senderName={sender?.display_name ?? `Gebruiker #${m.sender_id}`}
              senderHandle={sender?.username}
              showHeader={!sameSender}
              isMe={m.sender_id === me?.id}
              myUsername={myUsername}
              isChannel={conversation.kind === "channel"}
            />
          </Fragment>
        );
      })}
      {setTyping.length > 0 && (
        <div className="px-1 py-2 text-xs italic text-slate-500">
          {setTyping
            .map((id) => users[id]?.display_name ?? `Gebruiker #${id}`)
            .join(", ")}{" "}
          aan het typen…
        </div>
      )}
      </div>
    </div>
  );
}

function MessageItem({
  message,
  senderName,
  senderHandle,
  showHeader,
  isMe,
  myUsername,
  isChannel,
}: {
  message: Message;
  senderName: string;
  senderHandle?: string;
  showHeader: boolean;
  isMe: boolean;
  myUsername?: string;
  isChannel: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const setPendingShare = useStore((s) => s.setPendingShare);
  const setReplyTarget = useStore((s) => s.setReplyTarget);
  const usersMap = useStore((s) => s.users);
  const isDeleted = message.deleted_at !== null;
  const isEdited = message.edited_at !== null && !isDeleted;

  function startReply() {
    if (isDeleted) return;
    setReplyTarget(message.conversation_id, {
      messageId: message.id,
      senderName,
      preview: (message.body || "").replace(/\s+/g, " ").slice(0, 140),
    });
  }

  async function quickReact(emoji: string) {
    if (isDeleted) return;
    const mine = message.reactions.some(
      (r) => r.user_id === (useStore.getState().me?.id ?? -1) && r.emoji === emoji,
    );
    try {
      if (mine) {
        await api.removeReaction(message.id, emoji);
      } else {
        await api.addReaction(message.id, emoji);
      }
    } catch {
      /* socket-events updaten alsnog als 't 'r doorheen kwam */
    }
  }

  async function togglePin() {
    if (isDeleted || busy) return;
    setBusy(true);
    try {
      if (message.pinned) {
        await api.unpinMessage(message.id);
      } else {
        await api.pinMessage(message.id);
      }
      // Socket-event pin:added/removed werkt de store bij — geen
      // optimistische update nodig.
    } catch {
      setError("pin-actie mislukt");
    } finally {
      setBusy(false);
    }
  }

  async function scrollToReplied(targetId: number) {
    await scrollToMessage({
      conversationId: message.conversation_id,
      messageId: targetId,
    });
  }

  /** Open de SharePicker met de inhoud van dit bericht voorgeladen.
   *  Tekst gaat 1-op-1 mee als bijschrift; bijlagen worden opnieuw
   *  opgehaald (server koppelt 'n bijlage aan precies één bericht, dus
   *  doorsturen = download + reupload). De picker doet de rest. */
  async function forward() {
    if (isDeleted || forwarding) return;
    setForwarding(true);
    setError(null);
    try {
      const files: File[] = [];
      for (const a of message.attachments) {
        try {
          files.push(await attachmentToFile(a));
        } catch {
          /* één rotte bijlage mag de doorstuur-actie niet stoppen */
        }
      }
      setPendingShare({ text: message.body || null, files });
    } catch {
      setError("doorsturen mislukt");
    } finally {
      setForwarding(false);
    }
  }

  const time = new Date(message.created_at).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  function startEdit() {
    setDraft(message.body);
    setError(null);
    setEditing(true);
  }

  async function saveEdit() {
    const body = draft.trim();
    if (!body) return;
    if (body === message.body) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.editMessage(message.id, { body });
      setEditing(false);
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "window_expired"
          ? "Bewerken kan niet meer — bericht is te oud."
          : "Bewerken mislukt.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    setBusy(true);
    setError(null);
    try {
      await api.deleteMessage(message.id);
      // WS-event 'message:deleted' updatet de store; modal sluit zelf.
      setConfirmingDelete(false);
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "window_expired"
          ? "Verwijderen kan niet meer — bericht is te oud."
          : "Verwijderen mislukt.",
      );
    } finally {
      setBusy(false);
    }
  }

  // Swipe-to-reply: alleen op touchscreens. Horizontale beweging > 60px
  // naar rechts triggert dezelfde reply-flow als de ↩-knop; onder die
  // drempel snapt 't bericht visueel terug. We bewegen de inhoud via
  // CSS-transform zodat React niet bij elke pointer-tick rerendert.
  const swipeStartXRef = useRef<number | null>(null);
  const swipeContentRef = useRef<HTMLDivElement>(null);
  const SWIPE_TRIGGER = 60;
  const SWIPE_MAX = 90;

  // Pointer-capture zodat de move/up-events óók binnenkomen als de
  // touch boven 'n knop in de hover-toolbar of mention-pill startte —
  // anders zou een button-child het event consumeren en zou de swipe
  // sporadisch falen op precies de plekken die je vaak raakt.
  const swipePointerIdRef = useRef<number | null>(null);
  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.pointerType !== "touch" || isDeleted) return;
    swipeStartXRef.current = e.clientX;
    swipePointerIdRef.current = e.pointerId;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* Browser ondersteunt 't niet — val terug op default bubbling */
    }
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const startX = swipeStartXRef.current;
    if (startX === null) return;
    const dx = Math.max(0, Math.min(SWIPE_MAX, e.clientX - startX));
    const el = swipeContentRef.current;
    if (el) {
      el.style.transform = `translateX(${dx}px)`;
      // Toon de ↩-pijl evenredig met de swipe-afstand.
      const arrow = el.parentElement?.querySelector(":scope > span") as HTMLElement | null;
      if (arrow) arrow.style.opacity = String(Math.min(1, dx / SWIPE_TRIGGER));
    }
  }
  function onPointerEnd(e: React.PointerEvent<HTMLDivElement>) {
    const startX = swipeStartXRef.current;
    const pid = swipePointerIdRef.current;
    if (pid !== null) {
      try {
        e.currentTarget.releasePointerCapture(pid);
      } catch {
        /* */
      }
      swipePointerIdRef.current = null;
    }
    if (startX === null) return;
    const dx = e.clientX - startX;
    swipeStartXRef.current = null;
    const el = swipeContentRef.current;
    if (el) {
      el.style.transition = "transform 150ms";
      el.style.transform = "translateX(0)";
      const arrow = el.parentElement?.querySelector(":scope > span") as HTMLElement | null;
      if (arrow) arrow.style.opacity = "0";
      setTimeout(() => {
        if (el) el.style.transition = "";
      }, 200);
    }
    if (dx >= SWIPE_TRIGGER) startReply();
  }

  return (
    <div
      data-message-id={message.id}
      className={`group relative rounded transition ${showHeader ? "mt-3" : "mt-px"}`}
      // touch-action: pan-y zorgt dat de browser verticaal scrollen
      // door blijft sturen, maar de horizontale swipe-right voor reply
      // door onze pointer-handler laat opvangen. Zonder dit zou Android
      // de gebare als "horizontale scroll naar nul" interpreteren.
      style={{ touchAction: "pan-y" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
    >
      {/* Reply-indicator die tijdens een swipe links zichtbaar wordt.
          De buitenste overflow is bewust niet hidden — de hover-toolbar
          steekt 'n paar pixels rechts uit; alleen tijdens swipen
          schuift de inhoud zichtbaar mee. */}
      <span
        className="pointer-events-none absolute left-1 top-1/2 -translate-y-1/2 text-accent opacity-0 transition-opacity"
        aria-hidden="true"
      >
        ↩
      </span>
      <div ref={swipeContentRef} className="relative">
      {message.pinned && (
        <div className="mb-0.5 flex items-center gap-1 text-[10px] font-medium text-amber-400">
          <IconPin size={11} />
          <span>Vastgepind</span>
        </div>
      )}
      {!isDeleted && message.reply_to && (
        <button
          onClick={() => void scrollToReplied(message.reply_to!.id)}
          className="mb-0.5 ml-0.5 flex max-w-full items-start gap-1 rounded border-l-2 border-accent bg-slate-800/60 px-2 py-0.5 text-left text-xs hover:bg-slate-800"
          title="Naar origineel"
        >
          <span className="shrink-0 text-accent">↩</span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium text-slate-300">
              {message.reply_to.deleted
                ? "[bericht verwijderd]"
                : usersMap[message.reply_to.sender_id]?.display_name ??
                  `Gebruiker #${message.reply_to.sender_id}`}
            </span>
            {!message.reply_to.deleted && message.reply_to.body_preview && (
              <span className="block truncate text-slate-500">
                {message.reply_to.body_preview}
              </span>
            )}
          </span>
        </button>
      )}
      {showHeader && (
        <div className="mb-0.5 flex items-baseline gap-2">
          <span className={`text-sm font-medium ${isMe ? "text-purple-300" : "text-slate-200"}`}>
            {senderName}
          </span>
          {senderHandle && (
            <span className="text-xs text-slate-500">@{senderHandle}</span>
          )}
          <span className="text-xs text-slate-500">{time}</span>
        </div>
      )}
      {isDeleted ? (
        <div className="text-sm italic text-slate-500">[bericht verwijderd]</div>
      ) : editing ? (
        <div className="space-y-1">
          <textarea
            className="w-full resize-none rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100 outline-none focus:border-accent"
            rows={Math.min(5, Math.max(1, draft.split("\n").length))}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void saveEdit();
              }
              if (e.key === "Escape") setEditing(false);
            }}
            autoFocus
          />
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={() => void saveEdit()}
              disabled={busy}
              className="rounded bg-accent px-2 py-0.5 text-white hover:bg-accent-hover disabled:opacity-50"
            >
              Opslaan
            </button>
            <button
              onClick={() => setEditing(false)}
              className="rounded bg-slate-700 px-2 py-0.5 text-slate-200 hover:bg-slate-600"
            >
              Annuleer
            </button>
            {error && <span className="text-red-400">{error}</span>}
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <span
              className="message-body inline text-sm text-slate-100"
              dangerouslySetInnerHTML={{
                __html: renderMarkdown(message.body, { highlightSelf: myUsername }),
              }}
            />
            {isEdited && (
              <span
                className="ml-1 text-[10px] text-slate-500"
                title={`Bewerkt op ${new Date(message.edited_at!).toLocaleString()}`}
              >
                (bewerkt)
              </span>
            )}
            {confirmingDelete && (
              <div className="mt-1 flex items-center gap-2 rounded border border-red-800 bg-red-950/40 px-2 py-1 text-xs">
                <span className="text-red-200">Dit bericht verwijderen?</span>
                <button
                  onClick={() => void doDelete()}
                  disabled={busy}
                  className="rounded bg-red-600 px-2 py-0.5 font-medium text-white hover:bg-red-500 disabled:opacity-50"
                >
                  Ja, verwijderen
                </button>
                <button
                  onClick={() => {
                    setConfirmingDelete(false);
                    setError(null);
                  }}
                  className="rounded bg-slate-700 px-2 py-0.5 text-slate-200 hover:bg-slate-600"
                >
                  Annuleer
                </button>
                {error && <span className="ml-1 text-red-300">{error}</span>}
              </div>
            )}
          </div>
          {!isDeleted && !confirmingDelete && (
            <div className="absolute right-1 top-0 flex items-center rounded bg-slate-900/90 opacity-0 shadow group-hover:opacity-100">
              <div className="flex border-r border-slate-700 pr-0.5">
                {QUICK_REACT_EMOJI.map((e) => (
                  <button
                    key={e}
                    onClick={() => void quickReact(e)}
                    className="rounded px-1 text-base leading-none hover:bg-slate-700"
                    title={`Reageer met ${e}`}
                    aria-label={`Reageer met ${e}`}
                    disabled={busy}
                  >
                    {e}
                  </button>
                ))}
              </div>
              <button
                onClick={startReply}
                className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-slate-100"
                title="Antwoorden"
                aria-label="Antwoorden"
                disabled={busy}
              >
                <IconReply size={14} />
              </button>
              {isChannel && (
                <button
                  onClick={() => void togglePin()}
                  className={`rounded p-1 hover:bg-slate-700 ${
                    message.pinned ? "text-amber-300" : "text-slate-400 hover:text-slate-100"
                  }`}
                  title={message.pinned ? "Pin verwijderen" : "Vastpinnen"}
                  aria-label={message.pinned ? "Pin verwijderen" : "Vastpinnen"}
                  disabled={busy}
                >
                  <IconPin size={14} />
                </button>
              )}
              <button
                onClick={() => void forward()}
                className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-slate-100 disabled:opacity-50"
                title="Doorsturen naar…"
                aria-label="Doorsturen naar…"
                disabled={busy || forwarding}
              >
                {forwarding ? (
                  <span className="block w-[14px] text-center text-[10px]">…</span>
                ) : (
                  <IconForward size={14} />
                )}
              </button>
              {isMe && (
                <>
                  <button
                    onClick={startEdit}
                    className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-slate-100"
                    title="Bewerken"
                    aria-label="Bewerken"
                    disabled={busy}
                  >
                    <IconPencil size={14} />
                  </button>
                  <button
                    onClick={() => setConfirmingDelete(true)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-red-400"
                    title="Verwijderen"
                    aria-label="Verwijderen"
                    disabled={busy}
                  >
                    <IconTrash size={14} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
      {!isDeleted && message.attachments.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-2">
          {message.attachments.map((a) => (
            <AttachmentChip key={a.id} att={a} />
          ))}
        </div>
      )}
      {!isDeleted && message.poll && (
        <PollCard poll={message.poll} message={message} />
      )}
      {!isDeleted && message.location && <LocationCard location={message.location} />}
      {!isDeleted && <ReactionRow message={message} />}
      </div>
    </div>
  );
}

function AttachmentChip({
  att,
}: {
  att: { id: number; filename: string; mime: string; size: number; url: string };
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isImage = att.mime.startsWith("image/");
  const isAudio = att.mime.startsWith("audio/");
  const [inlineUrl, setInlineUrl] = useState<string | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const openLightbox = useStore((s) => s.openLightbox);

  // Voor afbeeldingen en audio fetchen we het bestand met Authorization-header,
  // bouwen een blob: URL en geven die door aan <img>/<audio>. Browsers kunnen
  // immers geen Bearer-token bij een gewone src= zetten. Blob wordt
  // hergebruikt door de download/open-knop zodat 'ie niet 2x over de lijn gaat.
  useEffect(() => {
    if (!isImage && !isAudio) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    (async () => {
      try {
        const { server_url, token } = loadSettings();
        const res = await fetch(server_url.replace(/\/$/, "") + att.url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        blobRef.current = blob;
        createdUrl = URL.createObjectURL(blob);
        setInlineUrl(createdUrl);
      } catch (err) {
        if (!cancelled) {
          console.error("[Swift] inline attachment load failed:", err);
          setError("laden mislukt");
        }
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [att.url, isImage, isAudio]);

  async function openOrDownload() {
    setBusy(true);
    setError(null);
    try {
      let blob = blobRef.current;
      if (!blob) {
        const { server_url, token } = loadSettings();
        const res = await fetch(server_url.replace(/\/$/, "") + att.url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        blob = await res.blob();
        blobRef.current = blob;
      }

      if (await isTauri()) {
        await saveAndOpenBlob(blob, att.filename);
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = att.filename;
        a.rel = "noopener";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
      }
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === "string"
          ? err
          : err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : "openen mislukt";
      console.error("[Swift] attachment open failed:", err);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  const sizeKB = Math.round(att.size / 1024);

  if (isImage) {
    return (
      <div className="flex flex-col gap-1">
        {inlineUrl ? (
          <button
            type="button"
            onClick={() => openLightbox(inlineUrl, att.filename)}
            className="overflow-hidden rounded-md border border-slate-700 bg-slate-800 hover:border-slate-500"
            title={`${att.filename} (${sizeKB} KB) — klik om te vergroten`}
          >
            <img
              src={inlineUrl}
              alt={att.filename}
              className="max-h-64 max-w-xs object-contain"
            />
          </button>
        ) : (
          <div className="flex h-24 w-40 items-center justify-center rounded-md border border-slate-700 bg-slate-800 text-xs text-slate-500">
            {error ?? "laden…"}
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500" title={att.filename}>
            {att.filename} · {sizeKB} KB
          </span>
          <button
            type="button"
            onClick={() => void openOrDownload()}
            disabled={busy}
            className="text-[10px] text-slate-400 underline hover:text-slate-200 disabled:opacity-50"
          >
            {busy ? "bezig…" : "downloaden"}
          </button>
        </div>
        {error && inlineUrl && <span className="text-[10px] text-red-400">{error}</span>}
      </div>
    );
  }

  if (isAudio) {
    return (
      <div className="flex flex-col gap-1">
        {inlineUrl ? (
          <audio
            src={inlineUrl}
            controls
            preload="metadata"
            className="h-9 w-72 max-w-full"
          />
        ) : (
          <div className="flex h-9 w-72 max-w-full items-center justify-center rounded-md border border-slate-700 bg-slate-800 text-xs text-slate-500">
            {error ?? "laden…"}
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500" title={att.filename}>
            {att.filename} · {sizeKB} KB
          </span>
          <button
            type="button"
            onClick={() => void openOrDownload()}
            disabled={busy}
            className="text-[10px] text-slate-400 underline hover:text-slate-200 disabled:opacity-50"
          >
            {busy ? "bezig…" : "downloaden"}
          </button>
        </div>
        {error && inlineUrl && <span className="text-[10px] text-red-400">{error}</span>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <button
        onClick={() => void openOrDownload()}
        disabled={busy}
        className="rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700 disabled:opacity-50"
        title={`${att.filename} (${sizeKB} KB)`}
      >
        📎 {busy ? "Bezig…" : att.filename}
      </button>
      {error && <span className="text-[10px] text-red-400">{error}</span>}
    </div>
  );
}
